/**
 * The PHONE activity map's ambient flow — the "Symphony Mobile.html" prototype's motion, ported as one pure model:
 * tags ride in from the channels tray and slide under the ring, and some come back out along a rail wire to their
 * category, while a short spark runs along each wire.
 *
 * ## One model, three twins
 *
 * iOS `ActivityMapFlow.swift`, Android `ActivityMapFlow.kt` and this file are the same model under the same names,
 * pinned to ONE set of expectations: `native-apps/shared/activity-map-flow/contract.json`. Change a constant or a
 * step here and in both natives together, and add the case there, never to one surface's test alone.
 *
 * ## Ambient, not a counter
 *
 * The prototype bumps its headline on every arrival; the product decided the headline stays the REAL count and
 * never ticks. Nor does the flow reflect the account's activity: like desktop's `CanvasDemoFlow`, it is made up on
 * a loop. An output's category is a uniform random pick (`pickCategory`), never weighted by the rail's counts (the
 * user's call, 2026-10-30, reversing the earlier weighting) — except while the rail's badges are FILLING.
 *
 * ## The badges fill from the flow
 *
 * Each time the map appears every rail badge starts empty, and an output that reaches its rail icon adds one to that
 * category's badge, until it shows the real count; after that an arrival changes nothing (`BadgeFill`, user decision
 * 2026-11-03). So a badge never moves without a visible arrival, and it fills at the flow's own pace — one output per
 * `outputEvery` at most. While a badge is short a new tag is aimed at a short category (`pickTarget`), counting the
 * tags already on their way so nothing overshoots; once every badge is full the pick is uniform again. The incoming glyphs come from the account's own channel chips (`incomingGlyphs`): which channels it
 * HAS, as desktop's tags leave its own channel plates, not what arrived on them. The Brief's wire gets a spark but
 * never an output.
 *
 * ## Screen points, from the board's own Scale
 *
 * Every path is built from the board's own `scaleTo(width, height)` (the `*OnScreen` helpers), never from the
 * prototype's coordinates, and every frame-unit constant goes through `scale.size(v)` (= v · sx). Seconds are
 * seconds. Pure and import-free of the app (only the layout module), so the unit tests read it directly.
 *
 * The simulation is deterministic given its injected `rng`: production passes `Math.random`, the contract a cycle.
 */
import {
  agentAngles,
  agentDot,
  rails,
  type Curve,
  type Pt,
  type Scale,
} from './phone-layout';

// ── constants (frame units go through `scale.size`; seconds are seconds)

/** Tags per wave, the gap between waves, when the first one comes, and the stagger inside a wave. */
export const tagsPerWave = 4;
export const waveEvery = 2.6;
export const firstWave = 0.6;
export const waveStagger = 0.14;
/** The share of incoming tags that are "bare": they look the same but yield no output when absorbed. */
export const plainShare = 0.4;
/** An incoming tag's speed (frame units/s), jittered per tag to `speedJitterMin … + speedJitterSpan`. */
export const travelSpeed = 70;
export const speedJitterMin = 0.85;
export const speedJitterSpan = 0.3;
/** Outputs travel this much faster than the base speed, unjittered. */
export const outSpeedFactor = 1.15;
/** At most one output released per this many seconds. */
export const outputEvery = 0.7;
/** An output reaching its rail icon pulses the icon (and its badge) — desktop's entry-point pulse on a label tag
 *  (`CanvasDemoFlow`'s `pulseArt`): scale 1 → this → 1, ease-out, over `arrivalPulseMs`. Every arrival, full or not. */
export const arrivalPulseScale = 1.18;
export const arrivalPulseMs = 500;
/** The longest frame step the sim takes: after a stall the scene slows down rather than jumping. */
export const maxDt = 0.05;
/** A tag's disc, its glyph box, and the glyph strokes in and out. */
export const tagSize = 22;
export const glyphSize = 12;
export const inGlyphStroke = 1.4;
export const outGlyphStroke = 1.6;
/** The in-path starts this far BEFORE the tray end of the channels wire, so a tag appears at the tray's edge… */
export const leadIn = 10;
/** …and runs on past the wire's ring end this share of the way to the ring's centre. */
export const tailShare = 0.6;
/** A tag is absorbed once it is this far inside the ring line (and past `absorbMinProgress` of its path). */
export const absorbInset = 16;
export const absorbMinProgress = 0.3;
/** An incoming tag fades in over this share of its path. */
export const fadeInShare = 0.08;
/** The spark: a short dash on the wire's own line, looping at `sparkSpeed` (frame units/s). */
export const sparkLength = 14;
export const sparkWidth = 1.6;
export const sparkSpeed = 120;
export const sparkFadeIn = 0.08;
export const sparkFadeOut = 0.9;
/** Spark j's start delay: `sparkDelayBase + j · sparkDelayStep + rng · sparkDelayJitter` (in-wire j 0, rails 1…7). */
export const sparkDelayBase = 0.4;
export const sparkDelayStep = 0.52;
export const sparkDelayJitter = 0.9;
/** An out-path starts at an agent's centre when one stands within this of the wire's first half. */
export const agentReach = 50;
/** Points a wire is sampled at (n + 1 points). */
export const samples = 48;

// ── inks: design-token NAMES (the renderer resolves them on the canvas), never literals

/** An incoming tag's disc: the page background the chips use (Surface/Standard, #F3F3F3). */
export const tagFillInk = 'var(--ds-surface-standard-bg)';
/** An incoming glyph's stroke. The frame draws #4F4F4F, which no web token carries; Text/Secondary (#5E5E5E) is
 *  the nearest, and it flips with dark mode. */
export const inGlyphInk = 'var(--ds-text-secondary)';
/** An output's glyph: black on every category (the prototype's white ink on goals/files was for darker wires). */
export const outGlyphInk = 'var(--ds-black)';
/** The in-wire's spark: #969696, the map divider's own neutral. A rail spark takes its wire's ink. */
export const inSparkInk = 'var(--ds-neutral-500)';
/** The tags' shadow: 0 1 3 black at this alpha, from the black token's RGB triplet. */
export const tagShadowRgb = 'var(--ds-black-rgb)';
export const tagShadowAlpha = 0.12;
export const tagShadowY = 1;
export const tagShadowBlur = 3;

// ── categories and glyphs

/** The six output categories, in the rail's order. Rail row `i` (1…6) is category `i − 1`; the Brief (0) has none. */
export type FlowCategory = 'tasks' | 'routines' | 'goals' | 'messages' | 'apps' | 'files';
export const flowCategories: readonly FlowCategory[] = ['tasks', 'routines', 'goals', 'messages', 'apps', 'files'];

/** The rail row an output of `category` runs to. */
export function railForCategory(category: FlowCategory): number {
  return flowCategories.indexOf(category) + 1;
}

/** An incoming tag's glyph — the KIND of channel, not its logo. */
export type InGlyph = 'phone' | 'mail' | 'chat' | 'browser';
export type FlowGlyph = InGlyph | FlowCategory;

/** The prototype's five sources (phone call, email, WhatsApp, website, message), for an account with no chips. */
export const fallbackGlyphs: readonly InGlyph[] = ['phone', 'mail', 'chat', 'browser', 'chat'];

/**
 * A category for a new tag, uniformly at random: `r` in [0, 1) picks one of the six. Never weighted by real data —
 * the flow is ambient, not the account's activity (see the file note).
 */
export function pickCategory(r: number): FlowCategory {
  return flowCategories[Math.min(flowCategories.length - 1, Math.floor(r * flowCategories.length))];
}

/** A count per output category. */
export type CategoryCounts = Record<FlowCategory, number>;

export function emptyCounts(): CategoryCounts {
  return { tasks: 0, routines: 0, goals: 0, messages: 0, apps: 0, files: 0 };
}

/**
 * The rail badges' fill (see the file note): what each badge SHOWS, and the real count it fills up to. Owned by the
 * map, one per appearance — a geometry change builds a new `Sim` but keeps the fill. `version` moves on every
 * change, so a host can redraw the badges only when it does. Keyed by the six output categories; the desktop
 * canvas's demo loop (`CanvasDemoFlow`) keys it by its own entry points.
 */
export class BadgeFill<K extends string = FlowCategory> {
  shown: Record<K, number>;
  targets: Record<K, number>;
  version = 0;

  constructor(readonly keys: readonly K[] = flowCategories as unknown as readonly K[]) {
    this.shown = Object.fromEntries(keys.map((k) => [k, 0])) as Record<K, number>;
    this.targets = { ...this.shown };
  }

  /** The real counts. A badge above its new count drops to it; one below keeps filling. */
  setTargets(targets: Partial<Record<K, number>>): void {
    for (const c of this.keys) {
      this.targets[c] = Math.max(0, Math.floor(targets[c] ?? 0));
      if (this.shown[c] > this.targets[c]) {
        this.shown[c] = this.targets[c];
        this.version += 1;
      }
    }
  }

  /** The categories still short once the tags already on their way land, in key order. */
  short(inFlight: Partial<Record<K, number>>): K[] {
    return this.keys.filter((c) => this.targets[c] - this.shown[c] - (inFlight[c] ?? 0) > 0);
  }

  /** An output reached `category`'s icon: one more on its badge, unless it is full. True when it moved. */
  arrive(category: K): boolean {
    if (this.shown[category] >= this.targets[category]) return false;
    this.shown[category] += 1;
    this.version += 1;
    return true;
  }

  /** Every badge at its real count at once — for a host whose flow cannot deliver (it has nothing to send). */
  complete(): void {
    for (const c of this.keys) {
      if (this.shown[c] !== this.targets[c]) {
        this.shown[c] = this.targets[c];
        this.version += 1;
      }
    }
  }

  get full(): boolean {
    return this.keys.every((c) => this.shown[c] >= this.targets[c]);
  }
}

/** A new tag's category: uniformly among the `short` ones while any is, else `pickCategory`'s uniform pick. */
export function pickTarget(r: number, short: readonly FlowCategory[]): FlowCategory {
  if (short.length === 0) return pickCategory(r);
  return short[Math.min(short.length - 1, Math.floor(r * short.length))];
}

/**
 * The incoming glyphs, one per channel chip in tray order (EVERY chip, folded ones included, no dedupe): the channels
 * the account has, not what arrived on them. A chip with no glyph is skipped; none at all falls back to the
 * prototype's five.
 */
export function incomingGlyphs(slugs: readonly string[]): InGlyph[] {
  const out: InGlyph[] = [];
  for (const slug of slugs) {
    if (slug === 'email-in') out.push('mail');
    else if (slug === 'whatsapp' || slug === 'sms-in') out.push('chat');
    else if (slug === 'web' || slug.startsWith('coder:')) out.push('browser');
    else if (slug === 'phone-app') out.push('phone');
  }
  return out.length ? out : [...fallbackGlyphs];
}

/** One glyph off the list for `r` in [0, 1). */
export function pickGlyph(glyphs: readonly InGlyph[], r: number): InGlyph {
  const list = glyphs.length ? glyphs : fallbackGlyphs;
  return list[Math.min(list.length - 1, Math.floor(r * list.length))];
}

/**
 * A glyph's strokes in its 16×16 viewBox (stroke only, no fill) — the prototype's own drawings. Each surface turns
 * them into its native path (web `Path2D`, Android `PathParser`, iOS by hand).
 */
export type GlyphShape =
  | { kind: 'rect'; x: number; y: number; w: number; h: number; rx: number }
  | { kind: 'circle'; cx: number; cy: number; r: number }
  | { kind: 'path'; d: string };

const chatShape: GlyphShape[] = [
  { kind: 'path', d: 'M14 9.4a1.6 1.6 0 0 1-1.6 1.6H5.2L2 14V3.6A1.6 1.6 0 0 1 3.6 2h8.8A1.6 1.6 0 0 1 14 3.6z' },
];

export const glyphViewBox = 16;
export const glyphShapes: Readonly<Record<FlowGlyph, readonly GlyphShape[]>> = {
  phone: [
    { kind: 'rect', x: 4.6, y: 1.6, w: 6.8, h: 12.8, rx: 1.6 },
    { kind: 'path', d: 'M7 12.4h2' },
  ],
  mail: [
    { kind: 'rect', x: 2, y: 3.5, w: 12, h: 9, rx: 1.6 },
    { kind: 'path', d: 'm2.6 4.6 5.4 4 5.4-4' },
  ],
  chat: chatShape,
  browser: [
    { kind: 'rect', x: 2, y: 3, w: 12, h: 10, rx: 1.6 },
    { kind: 'path', d: 'M2 6.4h12' },
  ],
  tasks: [{ kind: 'path', d: 'M3 8.4 6.4 12 13 4.6' }],
  routines: [
    {
      kind: 'path',
      d: 'M2.5 8a5.5 5.5 0 0 1 9.4-3.9L13.5 5.7M13.5 2.5v3.2h-3.2M13.5 8a5.5 5.5 0 0 1-9.4 3.9L2.5 10.3M2.5 13.5v-3.2h3.2',
    },
  ],
  goals: [
    { kind: 'circle', cx: 8, cy: 8, r: 5.6 },
    { kind: 'circle', cx: 8, cy: 8, r: 2.6 },
  ],
  messages: chatShape,
  apps: [
    { kind: 'rect', x: 2.2, y: 2.2, w: 4.6, h: 4.6, rx: 1 },
    { kind: 'rect', x: 9.2, y: 2.2, w: 4.6, h: 4.6, rx: 1 },
    { kind: 'rect', x: 2.2, y: 9.2, w: 4.6, h: 4.6, rx: 1 },
    { kind: 'rect', x: 9.2, y: 9.2, w: 4.6, h: 4.6, rx: 1 },
  ],
  files: [
    { kind: 'path', d: 'M9.3 1.8H4.2a1.4 1.4 0 0 0-1.4 1.4v9.6a1.4 1.4 0 0 0 1.4 1.4h7.6a1.4 1.4 0 0 0 1.4-1.4V5.7z' },
    { kind: 'path', d: 'M9.3 1.8v3.9h3.9' },
  ],
};

// ── polylines

export const dist = (a: Pt, b: Pt): number => Math.hypot(a.x - b.x, a.y - b.y);

/** The cubic at t = i/n, i = 0…n (n + 1 points). */
export function sampleCubic(c: Curve, n: number = samples): Pt[] {
  const out: Pt[] = [];
  for (let i = 0; i <= n; i += 1) {
    const t = i / n;
    const u = 1 - t;
    const a = u * u * u;
    const b = 3 * u * u * t;
    const d = 3 * u * t * t;
    const e = t * t * t;
    out.push({
      x: a * c.start.x + b * c.c1.x + d * c.c2.x + e * c.end.x,
      y: a * c.start.y + b * c.c1.y + d * c.c2.y + e * c.end.y,
    });
  }
  return out;
}

/** The sum of a polyline's segment lengths. */
export function polylineLength(points: readonly Pt[]): number {
  let total = 0;
  for (let i = 1; i < points.length; i += 1) total += dist(points[i - 1], points[i]);
  return total;
}

/** The point `distance` along a polyline, interpolated linearly and clamped to [0, length]. */
export function pointAt(points: readonly Pt[], distance: number): Pt {
  if (points.length === 0) return { x: 0, y: 0 };
  if (distance <= 0) return { ...points[0] };
  let walked = 0;
  for (let i = 1; i < points.length; i += 1) {
    const seg = dist(points[i - 1], points[i]);
    if (walked + seg >= distance) {
      const f = seg > 0 ? (distance - walked) / seg : 0;
      return { x: points[i - 1].x + (points[i].x - points[i - 1].x) * f, y: points[i - 1].y + (points[i].y - points[i - 1].y) * f };
    }
    walked += seg;
  }
  return { ...points[points.length - 1] };
}

/** The stretch of a polyline between two distances along it — what a spark's dash is drawn on. */
export function polylineSlice(points: readonly Pt[], from: number, to: number): Pt[] {
  if (points.length === 0 || to <= from) return [];
  const out: Pt[] = [pointAt(points, from)];
  let walked = 0;
  for (let i = 1; i < points.length; i += 1) {
    walked += dist(points[i - 1], points[i]);
    if (walked > from && walked < to) out.push({ ...points[i] });
    if (walked >= to) break;
  }
  out.push(pointAt(points, to));
  return out;
}

// ── the paths

export interface FlowPolyline {
  points: Pt[];
  length: number;
}

export interface OutPath extends FlowPolyline {
  /** The rail row (0 brief … 6 files). */
  rail: number;
  /** The agent the path starts at, or −1 when none stood within reach of the wire. */
  agent: number;
}

export interface FlowPaths {
  /** Lead-in + the channels wire + the tail toward the ring's centre: what an incoming tag rides. */
  in: FlowPolyline;
  /** The channels wire alone, sampled: what the in-wire's spark runs on. */
  inWire: FlowPolyline;
  /** Per rail row 0…6: agent centre (when in reach) + the wire + the rail icon's centre. */
  out: OutPath[];
  /** Per rail row 0…6, the wire alone, sampled: what its spark runs on. */
  outWires: FlowPolyline[];
  ringCentre: Pt;
  ringRadius: number;
  /** Where the agents stand — the out layer's holes — and where the rail's icons sit (its clip's edge, and every
   *  out-path's last point). */
  agentCentres: Pt[];
  railIconCentres: Pt[];
}

const line = (points: Pt[]): FlowPolyline => ({ points, length: polylineLength(points) });

/** Every path the flow runs on, in SCREEN points, for a board laid out by `scale` with `agents` dots on its ring. */
export function buildPaths(scale: Scale, agents: number): FlowPaths {
  const ringCentre = scale.ringCentre();
  const ringRadius = scale.ringRadius();

  // In: the channels tray's wire (authored tray→orbit), a lead-in back along its first tangent, and a straight
  // tail on toward the ring's centre. The tools tray's wire is never animated.
  const c = scale.clusterWireOnScreen('channels', agents);
  const wireIn = sampleCubic(c);
  const dx = wireIn[1].x - wireIn[0].x;
  const dy = wireIn[1].y - wireIn[0].y;
  const norm = Math.hypot(dx, dy) || 1;
  const lead = { x: wireIn[0].x - (dx / norm) * scale.size(leadIn), y: wireIn[0].y - (dy / norm) * scale.size(leadIn) };
  const inside = { x: c.end.x + tailShare * (ringCentre.x - c.end.x), y: c.end.y + tailShare * (ringCentre.y - c.end.y) };

  const agentCentres = agentAngles(agents).map((a) => scale.ringPointOnScreen(a));
  const railIconCentres = rails.map((_, i) => scale.railIconCentreOnScreen(i));
  const reach = scale.size(agentReach);

  const outWires: FlowPolyline[] = [];
  const out: OutPath[] = rails.map((_, i) => {
    const wire = sampleCubic(scale.wireOnScreen(i, agents));
    outWires.push(line(wire));
    // The nearest agent to the wire's first half (ties: lowest agent, then lowest sample) — the output emerges
    // from under it; the samples before its nearest point are dropped.
    let best = Infinity;
    let bestA = -1;
    let bestK = 0;
    for (let a = 0; a < agentCentres.length; a += 1) {
      for (let k = 0; k <= samples / 2; k += 1) {
        const d = dist(agentCentres[a], wire[k]);
        if (d < best) {
          best = d;
          bestA = a;
          bestK = k;
        }
      }
    }
    const inReach = bestA >= 0 && best <= reach;
    const points = inReach ? [agentCentres[bestA], ...wire.slice(bestK)] : [...wire];
    points.push(railIconCentres[i]);
    return { rail: i, agent: inReach ? bestA : -1, ...line(points) };
  });

  return {
    in: line([lead, ...wireIn, inside]),
    inWire: line(wireIn),
    out,
    outWires,
    ringCentre,
    ringRadius,
    agentCentres,
    railIconCentres,
  };
}

/** The out layer's hole at each agent: the agent's own half-width, so an output emerges from under its dot. */
export function agentHoleRadius(scale: Scale): number {
  return scale.size(agentDot / 2);
}

/**
 * The out layer's right edge, in screen points: the rail icons' centre line. The out layer is drawn UNDER the rail
 * tiles (user decision 2026-11-03, as desktop's tags slide under an entry point's art), so an output runs on into its
 * icon's art and is hidden by it; nothing of it is drawn right of the icon's centre, where the art no longer covers it.
 * This replaced a circular hole at each rail icon (icon half + a 1.5 pad), which made a tag vanish into the background
 * some 8 points before it reached the art — a "grey disc" over the wire's end.
 */
export function outLayerClipX(paths: Pick<FlowPaths, 'railIconCentres'>): number {
  return Math.min(...paths.railIconCentres.map((p) => p.x));
}

// ── looks

/** An incoming tag's opacity and scale at progress `p` (outputs are always 1 and 1). */
export function tagLook(p: number): { opacity: number; scale: number } {
  return { opacity: Math.min(1, p / fadeInShare), scale: 0.9 + 0.1 * Math.min(1, 4 * p) };
}

/**
 * Where a wire's spark is at sim time `t`: it loops start→end at `speed` along the wire's own `length`, from
 * `delay` on, fading in over its first `sparkFadeIn` and out after `sparkFadeOut`. The dash is the wire's stretch
 * from `max(0, head − size(sparkLength))` to `head`.
 */
export function sparkAt(t: number, length: number, delay: number, speed: number): { visible: boolean; head: number; opacity: number } {
  const local = t - delay;
  if (local < 0 || !(length > 0) || !(speed > 0)) return { visible: false, head: 0, opacity: 0 };
  const period = length / speed;
  const f = (local % period) / period;
  const opacity = f < sparkFadeIn ? f / sparkFadeIn : f > sparkFadeOut ? (1 - f) / (1 - sparkFadeOut) : 1;
  return { visible: true, head: f * length, opacity };
}

// ── the simulation

export interface InTag {
  p: number;
  speed: number;
  category: FlowCategory;
  bare: boolean;
  glyph: InGlyph;
}

export interface OutTag {
  rail: number;
  p: number;
}

/**
 * The flow's state and its one step. `glyphs` may be replaced live (`setGlyphs`) without a reset; a geometry change
 * (resize, a tray opening, the agent count) builds new paths and a new Sim.
 */
export class Sim {
  t = 0;
  nextWave = firstWave;
  pending: number[] = [];
  ins: InTag[] = [];
  queue: FlowCategory[] = [];
  lastRelease = -Infinity;
  outs: OutTag[] = [];
  spawned = 0;
  released = 0;
  /** Outputs that reached their rail icon and moved its badge. */
  arrived = 0;
  /** Outputs that reached their rail icon, badge moved or not — each one pulses the icon. */
  landed = 0;
  /** The categories whose rail icon an output reached in the LAST step, in order: the host pulses them. */
  justLanded: FlowCategory[] = [];
  /** Each spark's start delay: the in-wire's first, then rails 0…6. */
  readonly sparkDelays: number[];

  constructor(
    readonly paths: FlowPaths,
    private glyphs: readonly InGlyph[],
    readonly scale: Scale,
    private readonly rng: () => number,
    /** The rail badges' fill, when the map shows them filling. */
    private readonly fill?: BadgeFill,
  ) {
    this.sparkDelays = Array.from({ length: 1 + paths.out.length }, (_, j) => sparkDelayBase + j * sparkDelayStep + rng() * sparkDelayJitter);
  }

  setGlyphs(glyphs: readonly InGlyph[]): void {
    this.glyphs = glyphs;
  }

  step(rawDt: number): void {
    // 1. The clock, clamped.
    const dt = Math.min(Math.max(rawDt, 0), maxDt);
    this.t += dt;
    this.justLanded = [];

    // 2. Incoming tags advance; a tag deep enough inside the ring (or at its path's end) is absorbed.
    const { paths, scale } = this;
    const inLength = paths.in.length;
    const absorbAt = paths.ringRadius - scale.size(absorbInset);
    const keptIns: InTag[] = [];
    for (const tag of this.ins) {
      tag.p += (tag.speed * dt) / inLength;
      const absorbed =
        (tag.p > absorbMinProgress && dist(pointAt(paths.in.points, tag.p * inLength), paths.ringCentre) < absorbAt) || tag.p >= 1;
      if (absorbed) {
        if (!tag.bare) this.queue.push(tag.category);
      } else {
        keptIns.push(tag);
      }
    }
    this.ins = keptIns;

    // 3. Outputs advance; one that reaches its rail icon pulses it (`justLanded`) and adds one to that category's badge
    //    while it is filling.
    const outSpeed = scale.size(travelSpeed) * outSpeedFactor;
    this.outs = this.outs.filter((o) => {
      o.p += (outSpeed * dt) / paths.out[o.rail].length;
      if (o.p < 1) return true;
      const category = flowCategories[o.rail - 1];
      this.landed += 1;
      this.justLanded.push(category);
      if (this.fill?.arrive(category)) this.arrived += 1;
      return false;
    });

    // 4. At most one release per `outputEvery`.
    if (this.queue.length > 0 && this.t - this.lastRelease >= outputEvery) {
      const category = this.queue.shift()!;
      this.outs.push({ rail: railForCategory(category), p: 0 });
      this.lastRelease = this.t;
      this.released += 1;
    }

    // 5. A wave is due: schedule its tags, staggered.
    if (this.t >= this.nextWave) {
      this.nextWave = this.t + waveEvery;
      for (let i = 0; i < tagsPerWave; i += 1) this.pending.push(i * waveStagger);
    }

    // 6. Count the pending delays down (the ones just scheduled included); spawn each that has run out.
    const stillPending: number[] = [];
    for (const delay of this.pending) {
      const left = delay - dt;
      if (left <= 0) this.spawn();
      else stillPending.push(left);
    }
    this.pending = stillPending;
  }

  /** The outputs on their way, per category: every tag in that will yield one, the queue, and the outputs out. */
  inFlight(): CategoryCounts {
    const n = emptyCounts();
    for (const tag of this.ins) if (!tag.bare) n[tag.category] += 1;
    for (const c of this.queue) n[c] += 1;
    for (const o of this.outs) n[flowCategories[o.rail - 1]] += 1;
    return n;
  }

  /** A new tag at p 0 — the rng draws in this order: speed, category, bare, glyph. It moves from the next step. */
  private spawn(): void {
    const speed = this.scale.size(travelSpeed) * (speedJitterMin + speedJitterSpan * this.rng());
    const category = pickTarget(this.rng(), this.fill ? this.fill.short(this.inFlight()) : []);
    const bare = this.rng() < plainShare;
    const glyph = pickGlyph(this.glyphs, this.rng());
    this.ins.push({ p: 0, speed, category, bare, glyph });
    this.spawned += 1;
  }
}
